import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import Button from "@atlaskit/button";
import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlassian/wac-ufo-segment";

import { Picture } from "@/components/wpl/image/src";
import { Video } from "@/components/wpl/video/src";
import { Row } from "@/libraries/wpl/foundations/src/grid";
import { TypographyStyles } from "@/libraries/wpl/foundations/src/typography-styles";

export const HeroTeam24Promotion = () => {
    const intl = useIntl();

    const MOBILE_STATIC_IMG_SRC =
        "//wac-cdn.atlassian.com/misc-assets/webp-images/team-24-promotion-header-static-mobile.webp";
    const TABLET_STATIC_IMG_SRC =
        "//wac-cdn.atlassian.com/misc-assets/webp-images/team-24-promotion-header-static-tablet.webp";
    const DESKTOP_STATIC_IMG_SRC =
        "//wac-cdn.atlassian.com/misc-assets/webp-images/team-24-promotion-header-static-desktop.webp";
    const VIDEO_POSTER_IMG_SRC =
        "//wac-cdn.atlassian.com/misc-assets/webp-images/team-24-promotion-header-poster.webp-1047714346.webp";
    const fallbackImageAltTxt = intl.formatMessage({
        id: "HomePage.Team24Hero.fallbackImageAltTxt",
        defaultMessage: "speaker at team 24 conference"
    });
    const logoAltTxt = intl.formatMessage({
        id: "HomePage.Team24Hero.team24AttributionLogoAltTxt",
        defaultMessage: "logo for Atlassian Team '24"
    });

    const heroStyles = css({
        "position": "relative",
        "paddingTop": "48px",
        "paddingBottom": "48px",
        "display": "flex",
        "flexDirection": "column",
        "gap": "30px",
        "maxWidth": "1648px",
        "margin": "0 auto",
        [media.below.sm]: {
            textAlign: "center"
        },

        [media.above.sm]: {
            textAlign: "left",
            gap: "32px"
        },
        [media.above.md]: {
            paddingBlockStart: "64px",
            paddingBlockEnd: "118px"
        },

        ".event-details": {
            marginBlockStart: "0",
            marginBlockEnd: "0"
        },
        ".dates": {
            display: "block",
            marginBlockEnd: "16px",
            [media.above.md]: {
                display: "inline",
                marginBlockEnd: 0
            }
        },
        ".divider": {
            display: "none",
            [media.above.md]: {
                display: "inline",
                fontSize: "24px",
                marginInlineStart: "16px",
                marginInlineEnd: "16px"
            }
        },
        ".h5": {
            fontWeight: 400
        },

        ".video-wrapper": {
            "position": "absolute",
            "insetBlockStart": 0,
            "insetInlineStart": 0,
            "z-index": "-1",
            "height": "100%",
            "width": "100%",
            "minHeight": "100%",
            "minWidth": "100%",
            "overflow": "hidden"
        },

        ".mobile-video": {
            display: "block",
            width: "100%",
            [media.above.sm]: {
                display: "none"
            }
        },
        ".desktop-video": {
            display: "none",
            [media.above.sm]: {
                "display": "block",
                "position": "absolute",
                "insetInlineStart": 0,
                "insetBlockStart": 0,
                "insetInlineEnd": 0,
                "insetBlockEnd": 0,
                "height": "100%",
                "minHeight": "100%",
                "width": "100%",
                "objectFit": "cover",
                "z-index": "-1"
            }
        },
        ".content-container": {
            display: "flex",
            flexDirection: "column",
            gap: "30px",
            [media.only.sm]: {
                gap: "32px",
                width: "47.5%",
                paddingBlockEnd: "12px"
            },
            [media.above.md]: {
                width: "40.5%",
                gap: "32px"
            }
        },
        ".fallback-img--desktop": {
            display: "none",
            [media.above.md]: {
                display: "block",
                width: "auto",
                position: "absolute",
                insetInlineEnd: 0,
                // override <Picture /> inline maxWidth style
                maxWidth: "60% !important",
                height: "auto",
                maxHeight: "100%",
                insetBlockStart: "50%",
                transform: "translateY(-50%)"
            }
        },
        ".fallback-img--tablet": {
            display: "none",
            [media.only.sm]: {
                display: "block",
                height: "100%",
                width: "auto",
                position: "absolute",
                insetBlockStart: 0,
                insetInlineEnd: 0
            }
        },
        ".header": {
            marginBlockStart: "0",
            marginBlockEnd: 0
        },
        ".logo-desktop": {
            display: "none",
            [media.above.sm]: {
                display: "block",
                width: "100%",
                maxWidth: "401px"
            }
        },
        ".logo-mobile": {
            display: "block",
            [media.above.sm]: {
                display: "none"
            }
        },
        ".sub-text": {
            marginBlockStart: 0,
            marginBlockEnd: 0,
            // overriding rebrand font styles https://www.figma.com/file/RZRs55BkCWtX6zBPUD7dRb/Type-Matrix-React---FY24-Updates?type=design&node-id=323-741&mode=design&t=YmwCpeikzGVJQ0LI-0
            fontFamily: `var(--bxp-charlie-text-font-family)`,
            [media.below.md]: {
                fontSize: "16px"
            }
        },
        "button": {
            alignSelf: "center",
            [media.above.sm]: {
                alignSelf: "baseline"
            }
        }
    });

    return (
        <UFOSegment name="hero">
            <TypographyStyles>
                <section className="hero-section" css={heroStyles}>
                    <div className="video-wrapper">
                        <Video
                            videoLink="//wac-cdn.atlassian.com/misc-assets/webp-images/CSD-8431-FY24-Team-24-Animated-Website-Hero_v03.webm"
                            videoType="webm"
                            className="desktop-video"
                            videoPreviewImage={VIDEO_POSTER_IMG_SRC}
                        >
                            <Picture
                                className="fallback-img--desktop"
                                image={{
                                    src: DESKTOP_STATIC_IMG_SRC,
                                    alt: fallbackImageAltTxt,
                                    aspectRatio: "none"
                                }}
                            />
                            <Picture
                                className="fallback-img--tablet"
                                image={{
                                    src: TABLET_STATIC_IMG_SRC,
                                    alt: fallbackImageAltTxt
                                }}
                            />
                        </Video>
                    </div>
                    <Row>
                        <div className="content-container">
                            <Picture
                                className="logo-desktop"
                                image={{
                                    src: "//wac-cdn.atlassian.com/misc-assets/webp-images/team-24_attribution-logo-above-left_full-color_RGB.svg",
                                    alt: logoAltTxt
                                }}
                            />

                            <Picture
                                className="logo-mobile"
                                image={{
                                    src: "//wac-cdn.atlassian.com/misc-assets/webp-images/team-24_attribution-logo-above-center_full-color_RGB.svg",
                                    alt: logoAltTxt
                                }}
                            />

                            <p className="event-details">
                                <span className="dates h5">
                                    {intl.formatMessage({
                                        id: "HomePage.Team24Hero.team24Dates",
                                        defaultMessage: "April 30 - May 2, 2024"
                                    })}
                                </span>
                                <span className="divider">|</span>
                                <span className="locations h5">
                                    {intl.formatMessage({
                                        id: "HomePage.Team24Hero.team24Location",
                                        defaultMessage:
                                            "Las Vegas and digitally"
                                    })}
                                </span>
                            </p>
                        </div>
                    </Row>
                    <Video
                        videoLink="//wac-cdn.atlassian.com/misc-assets/webp-images/Team-24-Animated-Website-Hero_Mobile_329x253.webm"
                        videoType="webm"
                        className="mobile-video"
                        videoPreviewImage={MOBILE_STATIC_IMG_SRC}
                    >
                        <Picture
                            className="fallback-img--mobile"
                            image={{
                                src: MOBILE_STATIC_IMG_SRC,
                                alt: fallbackImageAltTxt
                            }}
                        />
                    </Video>
                    <Row>
                        <div className="content-container">
                            <h1 className="header h3">
                                {intl.formatMessage({
                                    id: "HomePage.Team24Hero.mainHeaderText",
                                    defaultMessage:
                                        "Be the first to see how AI is powering the Atlassian portfolio to drive acceleration, efficiency, and excellence."
                                })}
                            </h1>
                            <p className="h5 sub-text">
                                {intl.formatMessage({
                                    id: "HomePage.Team24Hero.subText",
                                    defaultMessage:
                                        "For a limited time only, save over 45% on your conference pass."
                                })}
                            </p>
                            <Button appearance="primary">
                                {intl.formatMessage({
                                    id: "HomePage.Team24Hero.ctaButtonText",
                                    defaultMessage: "Register now"
                                })}
                            </Button>
                        </div>
                    </Row>
                </section>
            </TypographyStyles>
        </UFOSegment>
    );
};
